import React, { useState, useEffect, useCallback, useId } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { FileText, Tag, ClockCounterClockwise, X, Copy, Trash, FolderPlus, ChatCircle, Check, Image, CaretLeft, CaretRight, Plus, ArrowsClockwise } from '@phosphor-icons/react';
import api from '../services/api';
import { getItemAnalysis, getItemCategory, getEnrichmentOtherMusicLinks, getItemRawResponse, getItemSubcategories, getItemStructuredData, getPlatformName } from '../utils/itemHelpers';
import { useMusicLinks } from '../utils/musicService';
import { useScrollLock } from '../hooks/useScrollLock';
import MusicLinks from './MusicLinks';
import CopyableId from './CopyableId';
import StructuredData from './StructuredData';
import CollectionPickerModal from './CollectionPickerModal';
import ExplorationLinks from './ExplorationLinks';
import Modal from './Modal';

const TABS = [
  { id: 'details', label: 'Details', icon: DetailsIcon },
  { id: 'category', label: 'Category', icon: CategoryIcon },
  { id: 'versions', label: 'Versions', icon: VersionsIcon },
];

const CATEGORY_OPTIONS = [
  'App', 'Art', 'Book', 'Charity', 'Course', 'Educational Course',
  'Event', 'Food', 'Fundraiser', 'Game', 'Home Decor',
  'Home Improvement Product', 'Music', 'Paint', 'Person',
  'Research Paper', 'Service', 'Social Media', 'Social Media Platform',
  'Software', 'Stock', 'Other',
];

export default function ItemDetailModal({
  item, onClose, onDelete, onRefresh, onCategoryClick, onChatAbout, categoriesData,
  isLoadingDetails = false, isDeleting = false,
  // Navigation props
  onPrev, onNext, hasPrev = false, hasNext = false,
  itemIndex, totalItems,
}) {
  const titleId = useId();
  const [activeTab, setActiveTab] = useState('details');
  // Carousel state
  const [carouselIndex, setCarouselIndex] = useState(0);

  // Normalize item ID — chat items use item_id, gallery items use id
  const itemId = item.item_id || item.id;

  // COL-173: a successful re-analyze returns the fresh analysis inline (see
  // handleReanalyze) — stash it here so the modal reflects the new version
  // immediately, without waiting on a full item refetch from the parent.
  const [overrideAnalysis, setOverrideAnalysis] = useState(null);

  // Use shared helpers to extract fields from item, preferring a freshly
  // re-analyzed version when we have one.
  const analysis = overrideAnalysis || getItemAnalysis(item);
  const rawResponse = overrideAnalysis ? (overrideAnalysis.raw_response || {}) : getItemRawResponse(item);
  const likelySource = rawResponse.media_metadata?.likely_source || rawResponse.image_details?.likely_source;
  const initialCategory = getItemCategory(item) || '';
  const initialSubcategories = getItemSubcategories(item);

  const [category, setCategory] = useState(initialCategory);
  const [subcategories, setSubcategories] = useState(initialSubcategories);

  // Track original values to detect changes (must be reset when item changes)
  const [originalCategory, setOriginalCategory] = useState(initialCategory);
  const [originalSubcategories, setOriginalSubcategories] = useState(initialSubcategories);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error' | null
  const [analyses, setAnalyses] = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showCollectionPicker, setShowCollectionPicker] = useState(false);
  const [collectionToast, setCollectionToast] = useState(null);

  // Re-analyze state (COL-173)
  const [reanalyzing, setReanalyzing] = useState(false);
  const [reanalyzeError, setReanalyzeError] = useState(null);
  const [reanalyzeElapsedSec, setReanalyzeElapsedSec] = useState(0);

  // Refs for timeout cleanup
  const copyTimeoutRef = React.useRef(null);
  const saveStatusTimeoutRef = React.useRef(null);
  const collectionToastRef = React.useRef(null);

  // Guards for the in-flight re-analyze request: which item it belongs to
  // (double-submit guard, itemId-scoped rather than a plain boolean so
  // navigating to a different item while one is in flight doesn't block a
  // re-analyze there too) and the currently-displayed item, kept live via
  // an effect so an async response can tell whether it's still relevant
  // before touching state (mirrors the `cancelled` flag pattern used for
  // the item-detail fetch in Gallery.jsx).
  const reanalyzeInFlightIdRef = React.useRef(null);
  const currentItemIdRef = React.useRef(itemId);
  useEffect(() => {
    currentItemIdRef.current = itemId;
  }, [itemId]);

  // Carousel images from the item
  const carouselImages = item.carousel_images && item.carousel_images.length > 1
    ? item.carousel_images
    : null;
  const safeCarouselIndex = carouselImages
    ? Math.min(carouselIndex, carouselImages.length - 1)
    : 0;

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      if (saveStatusTimeoutRef.current) clearTimeout(saveStatusTimeoutRef.current);
      if (collectionToastRef.current) clearTimeout(collectionToastRef.current);
    };
  }, []);

  // Lock body scroll when modal is open using body-scroll-lock library
  // Handles iOS Safari, scrollbar compensation, and touch events
  const { targetRef: modalRef, unlock: unlockScroll } = useScrollLock(true);

  // Wrap onClose to unlock scroll immediately before the exit animation starts
  const handleClose = useCallback(() => {
    unlockScroll();
    onClose();
  }, [onClose, unlockScroll]);

  // Keyboard handler: arrows for carousel/item navigation. Escape-to-close
  // is handled by the shared <Modal> primitive.
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't navigate when typing in inputs
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (carouselImages && carouselIndex > 0) {
          // Navigate carousel backward
          setCarouselIndex(i => i - 1);
        } else if (onPrev && hasPrev) {
          // At first carousel image or no carousel — go to previous item
          onPrev();
        }
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (carouselImages && carouselIndex < carouselImages.length - 1) {
          // Navigate carousel forward
          setCarouselIndex(i => i + 1);
        } else if (onNext && hasNext) {
          // At last carousel image or no carousel — go to next item
          onNext();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [carouselImages, carouselIndex, onPrev, onNext, hasPrev, hasNext]);

  // Use image from item if available (it's already a presigned S3 URL)
  const imageUrl = item.image || null;

  // Reset state when item changes (fixes stale state when opening different items)
  useEffect(() => {
    const newCategory = getItemCategory(item) || '';
    const newSubcategories = getItemSubcategories(item);

    setCategory(newCategory);
    setSubcategories(newSubcategories);
    // Reset original values so hasChanges computes correctly for new item
    setOriginalCategory(newCategory);
    setOriginalSubcategories(newSubcategories);
    setActiveTab('details');
    setAnalyses([]);
    setSaveStatus(null);
    setCarouselIndex(0);
    setOverrideAnalysis(null);
    setReanalyzeError(null);
  }, [itemId]);

  // Tick the elapsed-time counter while a re-analyze is in flight, for the
  // honest "still working (Ns)…" copy below — purely cosmetic, never gates
  // whether/when the request actually resolves.
  useEffect(() => {
    if (!reanalyzing) return;
    const start = Date.now();
    const interval = setInterval(() => {
      setReanalyzeElapsedSec(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [reanalyzing]);

  // Compute whether there are unsaved changes
  const hasChanges = React.useMemo(() => {
    if (category !== originalCategory) return true;
    if (subcategories.length !== originalSubcategories.length) return true;
    return subcategories.some((s, i) => s !== originalSubcategories[i]);
  }, [category, subcategories, originalCategory, originalSubcategories]);

  // Define loadVersions before the effect that uses it
  const loadVersions = useCallback(async () => {
    setLoadingVersions(true);
    try {
      const { analyses: data } = await api.getItemAnalyses(itemId);
      setAnalyses(data);
    } catch (err) {
      console.error('Failed to load versions:', err);
    } finally {
      setLoadingVersions(false);
    }
  }, [itemId]);

  // Load versions when tab is activated
  useEffect(() => {
    if (activeTab === 'versions' && analyses.length === 0) {
      loadVersions();
    }
  }, [activeTab, analyses.length, loadVersions]);

  // Trigger on-demand re-analysis (COL-173). Rides the Lambda Function URL
  // (see ApiService.analyzeItem) so it's no longer capped at API Gateway's
  // ~29s timeout — a run can legitimately take 10-60s, so this needs to be
  // clearly disabled/in-progress rather than a button that appears dead.
  const handleReanalyze = useCallback(async () => {
    // Double-submit guard, scoped to this item — a stray click while a
    // request for THIS item is already in flight is a no-op; a different
    // item (reached via prev/next while this one is still running) is
    // unaffected.
    if (reanalyzeInFlightIdRef.current === itemId) return;
    reanalyzeInFlightIdRef.current = itemId;

    const requestItemId = itemId;
    setReanalyzing(true);
    setReanalyzeError(null);
    setReanalyzeElapsedSec(0);

    try {
      const response = await api.analyzeItem(requestItemId);

      // An analyzer failure is an HTTP 500 (api.analyzeItem throws on it);
      // the body check stays as a guard for any 200 that still carries one.
      if (response?.status === 'error') {
        throw new Error(response.message || 'Re-analysis failed');
      }

      // Ignore a response that arrives after the user navigated away from
      // this item — never let a stale result overwrite what's on screen.
      if (currentItemIdRef.current !== requestItemId) return;

      if (response?.analysis) {
        setOverrideAnalysis(response.analysis);
      }
      // Re-fetch the versions list directly (rather than clearing it and
      // relying on the tab-activation effect) so the new version shows up
      // even when the list was already empty/unfetched — the Re-analyze
      // button only ever renders while the Versions tab is active, so this
      // is always fetching for the tab currently on screen.
      loadVersions();
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Failed to re-analyze item:', err);
      if (currentItemIdRef.current === requestItemId) {
        setReanalyzeError(err.message || 'Re-analysis failed. Please try again.');
      }
    } finally {
      if (reanalyzeInFlightIdRef.current === requestItemId) {
        reanalyzeInFlightIdRef.current = null;
      }
      if (currentItemIdRef.current === requestItemId) {
        setReanalyzing(false);
      }
    }
  }, [itemId, onRefresh, loadVersions]);

  const handleCopyDetails = async () => {
    const details = `
${analysis.headline || 'Untitled'}

${analysis.summary || analysis.description || ''}

Category: ${analysis.category || 'Uncategorized'}
${analysis.raw_response?.topics?.length ? `Tags: ${analysis.raw_response.topics.join(', ')}` : ''}
    `.trim();

    try {
      await navigator.clipboard.writeText(details);
      setCopied(true);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const handleSaveCategory = async () => {
    setIsSaving(true);
    setSaveStatus(null);
    try {
      const updateData = {};
      if (category) updateData.category = category;
      // Send full subcategories array to API
      updateData.subcategories = subcategories;

      await api.updateItem(itemId, updateData);
      setSaveStatus('success');
      // Update originals so hasChanges becomes false and button disables
      setOriginalCategory(category);
      setOriginalSubcategories([...subcategories]);
      if (onRefresh) onRefresh();
      // Clear success message after 2 seconds
      if (saveStatusTimeoutRef.current) clearTimeout(saveStatusTimeoutRef.current);
      saveStatusTimeoutRef.current = setTimeout(() => setSaveStatus(null), 2000);
    } catch (err) {
      console.error('Failed to save category:', err);
      setSaveStatus('error');
    } finally {
      setIsSaving(false);
    }
  };

  // Subcategory management handlers
  const handleAddSubcategory = (newSubcategory) => {
    if (newSubcategory && !subcategories.includes(newSubcategory)) {
      setSubcategories([...subcategories, newSubcategory]);
    }
  };

  const handleRemoveSubcategory = (subcategoryToRemove) => {
    setSubcategories(subcategories.filter(s => s !== subcategoryToRemove));
  };

  // Extract tags from analysis
  const tags = rawResponse.topics ||
               rawResponse.enrichment_hint?.primary?.topics ||
               [];

  return (
    <>
      <Modal onClose={handleClose} labelledBy={titleId} panelRef={modalRef}>
        {/* Image Panel */}
        <div className="modal-image-panel">
          <button className="modal-close-image" onClick={handleClose}>
            <CloseIcon />
          </button>

          {/* Prev/Next item navigation arrows */}
          {hasPrev && onPrev && (
            <button
              className="modal-nav-arrow modal-nav-prev"
              onClick={(e) => { e.stopPropagation(); onPrev(); }}
              aria-label="Previous item"
            >
              <ChevronLeftIcon />
            </button>
          )}
          {hasNext && onNext && (
            <button
              className="modal-nav-arrow modal-nav-next"
              onClick={(e) => { e.stopPropagation(); onNext(); }}
              aria-label="Next item"
            >
              <ChevronRightIcon />
            </button>
          )}

          {/* Image or Carousel */}
          {carouselImages ? (
            <div className="modal-carousel">
              <img
                src={carouselImages[safeCarouselIndex]}
                alt={`${analysis.headline || 'Item'} (${safeCarouselIndex + 1}/${carouselImages.length})`}
              />
              {/* Carousel nav arrows */}
              {safeCarouselIndex > 0 && (
                <button
                  className="carousel-arrow carousel-arrow-left"
                  onClick={(e) => { e.stopPropagation(); setCarouselIndex(i => i - 1); }}
                  aria-label="Previous image"
                >
                  <ChevronLeftIcon />
                </button>
              )}
              {safeCarouselIndex < carouselImages.length - 1 && (
                <button
                  className="carousel-arrow carousel-arrow-right"
                  onClick={(e) => { e.stopPropagation(); setCarouselIndex(i => i + 1); }}
                  aria-label="Next image"
                >
                  <ChevronRightIcon />
                </button>
              )}
              {/* Counter */}
              <div className="carousel-counter">
                {safeCarouselIndex + 1} / {carouselImages.length}
              </div>
              {/* Dot indicators */}
              <div className="carousel-dots">
                {carouselImages.map((_, i) => (
                  <button
                    key={i}
                    className={`carousel-dot ${i === safeCarouselIndex ? 'active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setCarouselIndex(i); }}
                    aria-label={`Go to image ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          ) : imageUrl ? (
            <img src={imageUrl} alt={analysis.headline || 'Item'} />
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            }}>
              <ImagePlaceholderIcon />
            </div>
          )}

          {/* Item position indicator — shift up when carousel dots are visible */}
          {itemIndex !== undefined && totalItems !== undefined && (
            <div className={`modal-item-counter ${carouselImages ? 'modal-item-counter--with-carousel' : ''}`}>
              Item {itemIndex + 1} of {totalItems}
            </div>
          )}
        </div>

        {/* Details Panel */}
        <div className="modal-details-panel">
          <button
            className="modal-close-details"
            onClick={handleClose}
          >
            <CloseIcon />
          </button>

          <div className="modal-header">
            <h2 className="modal-title" id={titleId}>{analysis.headline || 'Untitled'}</h2>
            {rawResponse.media_metadata?.source_title && (
              <div className="modal-source">
                By {rawResponse.media_metadata.source_title}
              </div>
            )}
            {(item.source_url || rawResponse.media_metadata?.original_poster) && (
              <div className="modal-source" style={{ marginTop: '2px' }}>
                {item.source_url ? (
                  <a href={item.source_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', textDecoration: 'none' }}>
                    {rawResponse.media_metadata?.original_poster
                      ? `${rawResponse.media_metadata.original_poster} on ${getPlatformName(likelySource) || 'Source'}`
                      : `View original${likelySource ? ` on ${getPlatformName(likelySource)}` : ''}`}
                    {' '}↗
                  </a>
                ) : (
                  <span>{rawResponse.media_metadata.original_poster}</span>
                )}
              </div>
            )}
            {isLoadingDetails && (
              <div className="modal-loading-bar">
                <motion.div
                  className="modal-loading-bar-indicator"
                  initial={{ x: '-100%' }}
                  animate={{ x: '300%' }}
                  transition={{
                    repeat: Infinity,
                    duration: 1,
                    ease: 'easeInOut',
                  }}
                />
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="modal-tabs">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                className={`modal-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="modal-body">
            {activeTab === 'details' && (
              <DetailsTab
                analysis={analysis}
                tags={tags}
                onCategoryClick={onCategoryClick}
                subcategories={subcategories}
                onRemoveSubcategory={handleRemoveSubcategory}
                structuredData={getItemStructuredData(item)}
                enrichments={item.enrichments}
                itemId={item.item_id || item.id}
                traceId={analysis.trace_id}
              />
            )}

            {activeTab === 'category' && (
              <CategoryTab
                category={category}
                subcategories={subcategories}
                onCategoryChange={setCategory}
                onAddSubcategory={handleAddSubcategory}
                onRemoveSubcategory={handleRemoveSubcategory}
                onSave={handleSaveCategory}
                isSaving={isSaving}
                saveStatus={saveStatus}
                categoriesData={categoriesData}
                hasChanges={hasChanges}
              />
            )}

            {activeTab === 'versions' && (
              <VersionsTab
                analyses={analyses}
                loading={loadingVersions}
                currentId={analysis.analysis_id}
                onReanalyze={handleReanalyze}
                reanalyzing={reanalyzing}
                reanalyzeError={reanalyzeError}
                reanalyzeElapsedSec={reanalyzeElapsedSec}
              />
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button
              className="btn btn-secondary"
              onClick={handleCopyDetails}
            >
              <CopyIcon />
              {copied ? 'Copied!' : 'Copy Details'}
            </button>
            {onChatAbout && (
              <button
                className="btn btn-secondary"
                onClick={() => onChatAbout(item)}
              >
                <ChatIcon />
                Chat about this
              </button>
            )}
            <button
              className="btn-add-to-collection"
              onClick={() => setShowCollectionPicker(true)}
              title="Add to collection"
            >
              <FolderPlusIcon />
              Collection
            </button>
            <button
              className="btn btn-ghost btn-danger-text"
              onClick={onDelete}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <div className="loading-spinner" style={{ width: 16, height: 16 }} />
              ) : (
                <TrashIcon />
              )}
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>

        {collectionToast && (
          <div style={{
            position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)',
            background: 'var(--surface-inverse)', color: 'var(--text-inverse)', padding: '8px 16px',
            borderRadius: 'var(--radius-md)', fontSize: '0.875rem', zIndex: 200,
          }}>
            {collectionToast}
          </div>
        )}
      </Modal>

      <AnimatePresence>
        {showCollectionPicker && (
          <CollectionPickerModal
            items={[{ item_id: itemId, item_type: 'item' }]}
            onClose={() => setShowCollectionPicker(false)}
            onSuccess={(msg) => {
              setShowCollectionPicker(false);
              setCollectionToast(msg);
              if (collectionToastRef.current) clearTimeout(collectionToastRef.current);
              collectionToastRef.current = setTimeout(() => setCollectionToast(null), 3000);
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}

// Helper to safely extract hostname from URL
function getHostname(url) {
  try {
    return new URL(url).hostname.replace('www.', '');
  } catch {
    return url;
  }
}

const REFERENCES_CAP = 4;

// Renders the first N reference cards, with the rest behind a show-more toggle.
function ReferencesList({ enrichments }) {
  const [showAll, setShowAll] = useState(false);
  const hiddenCount = enrichments.length - REFERENCES_CAP;
  const visible = showAll ? enrichments : enrichments.slice(0, REFERENCES_CAP);

  return (
    <>
      <div className="enrichment-cards">
        {visible.map((enrichment) => (
          <EnrichmentCard key={enrichment.enrichment_id} enrichment={enrichment} />
        ))}
      </div>
      {hiddenCount > 0 && (
        <button
          className="enrichment-card-expand"
          onClick={() => setShowAll(!showAll)}
          aria-expanded={showAll}
        >
          {showAll ? '− Show fewer references' : `+ Show ${hiddenCount} more reference${hiddenCount > 1 ? 's' : ''}`}
        </button>
      )}
    </>
  );
}

// Enrichment Card Component
function EnrichmentCard({ enrichment }) {
  const [expanded, setExpanded] = useState(false);
  const { entity_name, entity_type, entity_subtype, primary_result, additional_results, place_data, creator_context, apple_music_url, spotify_url, exploration_links } = enrichment;

  const musicLinks = useMusicLinks({
    appleMusicUrl: apple_music_url,
    spotifyUrl: spotify_url,
    otherLinks: getEnrichmentOtherMusicLinks([enrichment]),
  });

  const hasPlaceData = place_data && entity_type === 'place';
  const entityLink = hasPlaceData && place_data.website
    ? place_data.website
    : (musicLinks[0]?.url || primary_result?.url);
  const snippet = primary_result?.snippet || '';

  // Build Google Maps URL from address
  const mapsUrl = hasPlaceData && place_data.formatted_address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(place_data.formatted_address)}`
    : primary_result?.url;

  return (
    <div className="enrichment-card">
      <div className="enrichment-card-header">
        {entityLink ? (
          <a href={entityLink} target="_blank" rel="noopener noreferrer" className="enrichment-card-entity">
            {entity_name}
          </a>
        ) : (
          <span className="enrichment-card-entity">{entity_name}</span>
        )}
        <MusicLinks links={musicLinks} classPrefix="enrichment-card" subject={entity_name} />
        <span className="enrichment-card-badge">{entity_subtype || entity_type.replace(/_/g, ' ')}</span>
      </div>

      {hasPlaceData && (
        <div className="enrichment-card-place-info">
          {place_data.formatted_address && (
            <a href={mapsUrl} target="_blank" rel="noopener noreferrer">
              {place_data.formatted_address}
            </a>
          )}
          {place_data.website && (
            <>
              {place_data.formatted_address && <span> · </span>}
              <a href={place_data.website} target="_blank" rel="noopener noreferrer">
                {getHostname(place_data.website)}
              </a>
            </>
          )}
          {place_data.phone_number && (
            <span> · {place_data.phone_number}</span>
          )}
          {place_data.rating && (
            <span> · ★ {place_data.rating}</span>
          )}
        </div>
      )}

      {/* COL-127: Directions/Reviews deep-links, see ExplorationLinks.jsx */}
      <ExplorationLinks links={exploration_links} compact />

      {creator_context && (
        <div className="enrichment-card-creator-context" style={{ fontStyle: 'italic', fontSize: '0.8125rem', color: 'var(--text-primary)', margin: '4px 0' }}>
          "{creator_context}"
        </div>
      )}

      {snippet && <div className="enrichment-card-snippet">"{snippet}"</div>}

      {additional_results?.length > 0 && (
        <>
          <button
            className="enrichment-card-expand"
            onClick={() => setExpanded(!expanded)}
            aria-expanded={expanded}
            aria-label={expanded ? 'Hide additional sources' : `Show ${additional_results.length} additional sources`}
          >
            {expanded ? '− Hide sources' : `+ Show ${additional_results.length} more source${additional_results.length > 1 ? 's' : ''}`}
          </button>
          {expanded && (
            <ul className="enrichment-card-sources">
              {additional_results.slice(0, 3).map((r, i) => (
                <li key={i}>
                  <a href={r.url} target="_blank" rel="noopener noreferrer">
                    {r.title || r.url} — {getHostname(r.url)}
                  </a>
                </li>
              ))}
              {additional_results.length > 3 && (
                <li className="enrichment-card-more">
                  <a href={primary_result?.url} target="_blank" rel="noopener noreferrer">
                    View all on web →
                  </a>
                </li>
              )}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

// Tab Components
function DetailsTab({ analysis, tags, onCategoryClick, subcategories = [], onRemoveSubcategory, structuredData, enrichments = [], itemId, traceId }) {
  const category = analysis.category || 'Uncategorized';

  const handleCategoryClick = () => {
    if (onCategoryClick && category !== 'Uncategorized') {
      onCategoryClick(category, null);
    }
  };

  const handleSubcategoryClick = (subcategory) => (e) => {
    e.stopPropagation();
    if (onCategoryClick && subcategory) {
      onCategoryClick(category, subcategory);
    }
  };

  return (
    <>
      {/* Category + Subcategories */}
      <div className="modal-section">
        <div className="modal-section-label">Category</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
          <button
            className="category-badge-btn"
            onClick={handleCategoryClick}
            disabled={category === 'Uncategorized'}
          >
            {category}
          </button>
          {subcategories.length > 0 && (
            <>
              <span style={{ color: 'var(--text-muted)' }}>›</span>
              {subcategories.map((subcategory, index) => (
                <div key={index} className="subcategory-chip">
                  <span
                    className="subcategory-chip-text"
                    onClick={handleSubcategoryClick(subcategory)}
                  >
                    {subcategory}
                  </span>
                  <button
                    className="subcategory-chip-remove"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveSubcategory(subcategory);
                    }}
                    aria-label={`Remove ${subcategory}`}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
            </>
          )}
        </div>
        {subcategories.length === 0 && (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', margin: '4px 0 0 0' }}>
            No subcategories. Use the Category tab to add some.
          </p>
        )}
      </div>

      {/* Summary */}
      <div className="modal-section">
        <div className="modal-section-label">Summary</div>
        <p className="modal-description">
          {analysis.summary || analysis.description || 'No summary available.'}
        </p>
      </div>

      {/* Tags */}
      {tags.length > 0 && (
        <div className="modal-section">
          <div className="modal-section-label">Tags</div>
          <div className="modal-tags">
            {tags.map((tag, i) => (
              <span key={i} className="modal-tag">{tag}</span>
            ))}
          </div>
        </div>
      )}

      {/* Structured Data */}
      {structuredData && (
        <div className="modal-section">
          <StructuredData structuredData={structuredData} />
        </div>
      )}

      {/* Enrichments / References */}
      {enrichments?.length > 0 && (
        <div className="modal-section">
          <div className="modal-section-label">References</div>
          <ReferencesList enrichments={enrichments} />
        </div>
      )}

      {/* Item ID */}
      {itemId && (
        <div className="modal-section">
          <div className="modal-section-label">Item ID</div>
          <CopyableId value={itemId} />
        </div>
      )}

      {/* Trace ID */}
      {traceId && (
        <div className="modal-section">
          <div className="modal-section-label">Trace ID</div>
          <CopyableId value={traceId} />
        </div>
      )}
    </>
  );
}

function CategoryTab({ category, subcategories = [], onCategoryChange, onAddSubcategory, onRemoveSubcategory, onSave, isSaving, saveStatus, categoriesData, hasChanges }) {
  const [categoryInput, setCategoryInput] = useState(category || '');
  const [subcategoryInput, setSubcategoryInput] = useState('');
  const [showCategorySuggestions, setShowCategorySuggestions] = useState(false);
  const [showSubcategorySuggestions, setShowSubcategorySuggestions] = useState(false);

  // Sync local state with props when they change
  useEffect(() => {
    setCategoryInput(category || '');
  }, [category]);

  // Get unique category names from API data
  const categoryOptions = React.useMemo(() => {
    if (!categoriesData?.categories) return CATEGORY_OPTIONS;
    return categoriesData.categories.map(c => c.name);
  }, [categoriesData]);

  // Get subcategory options based on selected category (excluding already added ones)
  const subcategoryOptions = React.useMemo(() => {
    if (!categoriesData?.subcategories || !category) return [];
    return categoriesData.subcategories
      .filter(s => s.category.toLowerCase() === category.toLowerCase())
      .map(s => s.name)
      .filter(s => !subcategories.includes(s));
  }, [categoriesData, category, subcategories]);

  // Filter suggestions based on input
  const filteredCategories = categoryOptions.filter(
    c => c.toLowerCase().includes(categoryInput.toLowerCase())
  );

  const filteredSubcategories = subcategoryOptions.filter(
    s => s.toLowerCase().includes(subcategoryInput.toLowerCase())
  );

  const handleCategoryInputChange = (value) => {
    setCategoryInput(value);
    setShowCategorySuggestions(true);
  };

  const handleCategorySelect = (value) => {
    setCategoryInput(value);
    onCategoryChange(value);
    setShowCategorySuggestions(false);
  };

  const handleCategoryBlur = () => {
    // Delay to allow click on suggestion
    setTimeout(() => {
      setShowCategorySuggestions(false);
      // If input doesn't match an existing category, treat it as a new category
      if (categoryInput) {
        onCategoryChange(categoryInput);
      }
    }, 150);
  };

  const handleSubcategoryInputChange = (value) => {
    setSubcategoryInput(value);
    setShowSubcategorySuggestions(true);
  };

  const handleSubcategoryAdd = (value) => {
    if (value && !subcategories.includes(value)) {
      onAddSubcategory(value);
      setSubcategoryInput('');
      setShowSubcategorySuggestions(false);
    }
  };

  const handleSubcategoryBlur = () => {
    setTimeout(() => {
      setShowSubcategorySuggestions(false);
    }, 150);
  };

  return (
    <div className="modal-section">
      {/* Category Input */}
      <div className="modal-section-label">Category</div>
      <div className="autocomplete-container">
        <input
          type="text"
          className="autocomplete-input"
          value={categoryInput}
          onChange={(e) => handleCategoryInputChange(e.target.value)}
          onFocus={() => setShowCategorySuggestions(true)}
          onBlur={handleCategoryBlur}
          placeholder="Type to search or add category..."
          style={{
            borderColor: 'var(--category-color)',
          }}
        />
        {showCategorySuggestions && filteredCategories.length > 0 && (
          <ul className="autocomplete-suggestions">
            {filteredCategories.map((cat) => (
              <li
                key={cat}
                className={`autocomplete-suggestion ${cat === category ? 'selected' : ''}`}
                onMouseDown={() => handleCategorySelect(cat)}
              >
                {cat}
              </li>
            ))}
            {categoryInput && !filteredCategories.some(c => c.toLowerCase() === categoryInput.toLowerCase()) && (
              <li
                className="autocomplete-suggestion new-category"
                onMouseDown={() => handleCategorySelect(categoryInput)}
              >
                <PlusSmallIcon />
                Create "{categoryInput}"
              </li>
            )}
          </ul>
        )}
      </div>

      {/* Current Subcategories */}
      <div className="modal-section-label" style={{ marginTop: 'var(--space-lg)' }}>Subcategories</div>
      {subcategories.length > 0 && (
        <div className="subcategory-chips" style={{ marginBottom: 'var(--space-md)' }}>
          {subcategories.map((sub, index) => (
            <div key={index} className="subcategory-chip">
              <span>{sub}</span>
              <button
                className="subcategory-chip-remove"
                onClick={() => onRemoveSubcategory(sub)}
                aria-label={`Remove ${sub}`}
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Subcategory Input */}
      <div className="autocomplete-container">
        <input
          type="text"
          className="autocomplete-input"
          value={subcategoryInput}
          onChange={(e) => handleSubcategoryInputChange(e.target.value)}
          onFocus={() => setShowSubcategorySuggestions(true)}
          onBlur={handleSubcategoryBlur}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && subcategoryInput) {
              e.preventDefault();
              handleSubcategoryAdd(subcategoryInput);
            }
          }}
          placeholder="Type to add a subcategory..."
        />
        {showSubcategorySuggestions && (filteredSubcategories.length > 0 || subcategoryInput) && (
          <ul className="autocomplete-suggestions">
            {filteredSubcategories.map((sub) => (
              <li
                key={sub}
                className="autocomplete-suggestion"
                onMouseDown={() => handleSubcategoryAdd(sub)}
              >
                {sub}
              </li>
            ))}
            {subcategoryInput && !subcategoryOptions.some(s => s.toLowerCase() === subcategoryInput.toLowerCase()) && !subcategories.some(s => s.toLowerCase() === subcategoryInput.toLowerCase()) && (
              <li
                className="autocomplete-suggestion new-category"
                onMouseDown={() => handleSubcategoryAdd(subcategoryInput)}
              >
                <PlusSmallIcon />
                Add "{subcategoryInput}"
              </li>
            )}
          </ul>
        )}
      </div>

      {/* Save Button */}
      <div style={{ marginTop: 'var(--space-lg)', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 'var(--space-md)' }}>
        {saveStatus === 'success' && (
          <span style={{ color: 'var(--success)', fontSize: '0.875rem', fontWeight: 500 }}>
            ✓ Saved successfully
          </span>
        )}
        {saveStatus === 'error' && (
          <span style={{ color: 'var(--danger)', fontSize: '0.875rem', fontWeight: 500 }}>
            Failed to save
          </span>
        )}
        <button
          className="btn btn-primary"
          onClick={onSave}
          disabled={isSaving || !hasChanges}
        >
          <CheckIcon />
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <p style={{
        marginTop: 'var(--space-lg)',
        fontSize: '0.8125rem',
        color: 'var(--text-muted)',
      }}>
        Add subcategories to help organize this item. Changes will update how this item appears in the sidebar filters.
      </p>
    </div>
  );
}

function PlusSmallIcon() {
  return <Plus size={14} style={{ marginRight: 4 }} />;
}

// Honest, elapsed-time-based staged copy for an in-flight re-analyze
// (spirit of COL-39's chat progress: real information only, never a fake
// step count — there's no server-side progress feed for /items/{id}/analyze
// to poll, so this is time-based rather than event-based).
function reanalyzeStatusText(elapsedSec) {
  if (elapsedSec < 3) return 'Sending to the analyzer…';
  if (elapsedSec < 15) return 'Analyzing…';
  if (elapsedSec < 30) return 'Still working — this can take up to a minute…';
  return `Still working (${elapsedSec}s)…`;
}

function ReanalyzeControl({ onReanalyze, reanalyzing, reanalyzeError, reanalyzeElapsedSec }) {
  return (
    <div className="modal-section reanalyze-bar-section">
      <div className="reanalyze-bar">
        <button
          className="btn btn-secondary"
          onClick={onReanalyze}
          disabled={reanalyzing}
          aria-busy={reanalyzing}
        >
          {reanalyzing ? (
            <div className="loading-spinner" style={{ width: 16, height: 16 }} />
          ) : (
            <ArrowsClockwise size={16} />
          )}
          {reanalyzing ? 'Re-analyzing…' : 'Re-analyze'}
        </button>
        {reanalyzing && (
          <span className="reanalyze-status">{reanalyzeStatusText(reanalyzeElapsedSec)}</span>
        )}
      </div>
      {reanalyzeError && <p className="reanalyze-msg reanalyze-msg-error">{reanalyzeError}</p>}
    </div>
  );
}

function VersionsTab({ analyses, loading, currentId, onReanalyze, reanalyzing, reanalyzeError, reanalyzeElapsedSec }) {
  const reanalyzeControl = (
    <ReanalyzeControl
      onReanalyze={onReanalyze}
      reanalyzing={reanalyzing}
      reanalyzeError={reanalyzeError}
      reanalyzeElapsedSec={reanalyzeElapsedSec}
    />
  );

  if (loading) {
    return (
      <>
        {reanalyzeControl}
        <div style={{ textAlign: 'center', padding: 'var(--space-xl)' }}>
          <div className="loading-spinner" style={{ margin: '0 auto' }} />
        </div>
      </>
    );
  }

  if (analyses.length === 0) {
    return (
      <>
        {reanalyzeControl}
        <p style={{ color: 'var(--text-muted)' }}>
          No analysis versions found.
        </p>
      </>
    );
  }

  return (
    <>
      {reanalyzeControl}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
      {analyses.map((a) => (
        <div
          key={a.analysis_id}
          style={{
            padding: 'var(--space-md)',
            background: a.analysis_id === currentId ? 'var(--accent-soft)' : 'var(--surface-sunken)',
            borderRadius: 'var(--radius-md)',
            border: a.analysis_id === currentId ? '1px solid var(--accent)' : '1px solid transparent',
          }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 'var(--space-xs)',
          }}>
            <span style={{ fontWeight: 500 }}>Version {a.version}</span>
            {a.analysis_id === currentId && (
              <span style={{
                fontSize: '0.6875rem',
                fontWeight: 600,
                color: 'var(--accent)',
                textTransform: 'uppercase',
              }}>
                Current
              </span>
            )}
          </div>
          <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
            {a.model_used || 'Unknown model'} &middot;{' '}
            {new Date(a.created_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}
          </div>
        </div>
      ))}
      </div>
    </>
  );
}

// Icons
function DetailsIcon() {
  return <FileText size={16} />;
}

function CategoryIcon() {
  return <Tag size={16} />;
}

function VersionsIcon() {
  return <ClockCounterClockwise size={16} />;
}

function CloseIcon() {
  return <X size={20} />;
}

function CopyIcon() {
  return <Copy size={16} />;
}

function TrashIcon() {
  return <Trash size={16} />;
}

function FolderPlusIcon() {
  return <FolderPlus size={16} />;
}

function ChatIcon() {
  return <ChatCircle size={16} />;
}

function CheckIcon() {
  return <Check size={16} />;
}

function ImagePlaceholderIcon() {
  return <Image size={64} style={{ color: 'rgba(255,255,255,0.3)' }} />;
}

function ChevronLeftIcon() {
  return <CaretLeft size={24} />;
}

function ChevronRightIcon() {
  return <CaretRight size={24} />;
}
